import React, {
  useCallback,
  useState,
  useEffect,
  useContext,
  useRef,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSocratesInterview } from "../../../hooks/useSocratesInterview";
import Prompt from "../../../components/input/Prompt/Prompt";
import Btn from "../../../components/btn";
import ProgressBar from "../../../components/ProgressBar";
import useSpeechToText from "../../../components/speech/useSpeechToText";
import { MyContext } from "../../../context/MyContext";
import { useDeviceGuard } from "../../../hooks/useDeviceGuard";
import { useAuth } from "../../tutorV2/hooks/useAuth";
import { useUserQuery } from "../../tutorV2/hooks/queries/useUserQuery";
import { Mode } from "../../tutorV2/types/tutor.types";
import { getConversationKey } from "../../../utils/contentDeviceCache";

const TIMER_DURATION = 120; // 120초

/**
 * SocratesPromptContainer
 * 소크라테스 답변 입력 컨테이너
 */
const SocratesPromptContainer: React.FC = () => {
  const { id: interviewId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    submitAnswer,
    loading,
    interview,
    messages,
    pauseInterview,
    resumeInterview,
    cloneInterview,
    generateResult,
    isGeneratingResult,
  } = useSocratesInterview();

  const { user, setIsOpenTicketAlert, isMobile } = useContext(MyContext);

  const [inputValue, setInputValue] = useState("");

  const { ensureDeviceRegistered, isCheckingDevice } = useDeviceGuard();
  const { showLoginModal } = useAuth();
  const { isLoggedIn } = useUserQuery();

  // submitAnswer는 현재 면접이 바뀌면 새로 만들어진다.
  // 초과 안내 → 기기 해제 → 재시도 경로는 예전 클로저로 실행되므로,
  // 재시도 시 예전 면접에 답변이 들어가지 않도록 최신 참조를 통해 호출한다.
  const submitAnswerRef = useRef(submitAnswer);
  const interviewIdRef = useRef(interviewId);

  useEffect(() => {
    submitAnswerRef.current = submitAnswer;
    interviewIdRef.current = interviewId;
  }, [submitAnswer, interviewId]);

  /**
   * 기기 등록 판정 후 답변 제출 (업무 #11104)
   * - 슬롯 초과/인증 만료 시 제출을 중단하며, 입력한 답변은 그대로 유지된다.
   * - 기기 해제 후 "다시 시도"하면 동일한 답변이 이어서 제출된다.
   *
   * @param onBeforeSubmit 판정 통과 후 제출 직전에 실행할 정리 동작 (입력창/타이머 초기화)
   */
  const submitAnswerWithDeviceGuard: (
    content: string,
    onBeforeSubmit?: () => void,
  ) => Promise<void> = useCallback(
    async (content, onBeforeSubmit) => {
      // 튜터와 동일하게 비로그인은 로그인 모달로 유도한다
      if (!isLoggedIn) {
        showLoginModal();
        return;
      }

      // 재시도 시점에 다른 면접으로 이동했다면 예전 답변을 제출하지 않는다
      const requestedInterviewId = interviewIdRef.current;

      // 판정은 면접 단위로 1회만 수행한다 (튜터의 대화 단위와 동일 원칙)
      const isDeviceRegistered = await ensureDeviceRegistered({
        conversationKey: getConversationKey(
          Mode.SOCRATES,
          requestedInterviewId,
        ),
        onRetry: () => {
          if (interviewIdRef.current !== requestedInterviewId) return;
          // Promise를 반환해야 모달이 재전송 완료까지 "확인 중" 상태를 유지한다
          return submitAnswerWithDeviceGuard(content, onBeforeSubmit);
        },
      });
      if (!isDeviceRegistered) return;
      if (interviewIdRef.current !== requestedInterviewId) return;

      onBeforeSubmit?.();
      await submitAnswerRef.current(content);
    },
    [ensureDeviceRegistered, isLoggedIn, showLoginModal],
  );

  // 티켓 검증
  const ticketCount = user?.socra_cnt;
  const isTicketExhausted = !ticketCount || ticketCount <= 0;
  const isFreeTrialExhausted = !user?.paid && isTicketExhausted;
  const isPossibleToUseService = !isTicketExhausted && !isFreeTrialExhausted;

  // Speech-to-Text hook
  const {
    listening,
    startListening,
    stopListening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechToText({
    onUpdateTranscript: (text: string) => {
      setInputValue(text);
    },
    onStopListening: (text: string) => {
      if (text.trim() && interview?.status === "active") {
        submitAnswerWithDeviceGuard(text, () => {
          setInputValue("");
          setShowTimer(false);
          resetTranscript();
        }).catch((error) => {
          console.error("답변 제출 실패:", error);
        });
      }
    },
    timeout: 2000,
    language: "ko-KR",
  });

  // 마이크 버튼 클릭 처리
  const handleMicClick = useCallback(() => {
    // if (!isPossibleToUseService) {
    //   setIsOpenTicketAlert(true);
    //   return;
    // }

    if (!browserSupportsSpeechRecognition) {
      console.error("Browser does not support speech recognition");
      return;
    }

    startListening();
  }, [browserSupportsSpeechRecognition, startListening]);

  // 음성 인식 닫기/중단 처리
  const handleCloseClick = useCallback(() => {
    stopListening();
    resetTranscript();
    setInputValue("");
  }, [stopListening, resetTranscript]);

  // 타이머 상태 (제어 버튼 표시를 위해 추가)
  const [showTimer, setShowTimer] = useState(false);
  const [timerKey, setTimerKey] = useState(0);
  const [prevLoading, setPrevLoading] = useState(loading);
  const [prevMessagesLength, setPrevMessagesLength] = useState(0);
  // 타이머 잔여 시간 저장 (면접 중단 시 저장, 이어서 시작 시 복원)
  const [savedTimeLeft, setSavedTimeLeft] = useState<number | null>(null);
  const [currentTimeLeft, setCurrentTimeLeft] = useState(TIMER_DURATION);
  // 이어서 시작 여부 (useEffect에서 타이머 리셋 방지용)
  const [isResuming, setIsResuming] = useState(false);

  // 타이머 시작 조건:
  // 1. API 응답 도착 시 (loading: true -> false)
  // 2. 첫 진입 시 메시지가 있고 active 상태일 때
  // 3. URL로 직접 접근 시 (interviewId가 있고, 메시지가 있고, active 상태)
  useEffect(() => {
    const messagesLength = messages?.length || 0;
    const isActiveStatus = interview?.status === "active";

    // 이어서 시작으로 인한 로딩 완료 시 - useEffect에서 타이머 시작하지 않음 (handleResume에서 처리)
    if (isResuming && prevLoading && !loading) {
      setIsResuming(false);
      setPrevLoading(loading);
      return;
    }

    // loading이 true→false로 바뀌고 메시지가 있을 때 (답변 후 새 질문)
    if (prevLoading && !loading && messagesLength > 0 && isActiveStatus) {
      setTimerKey((prev) => prev + 1);
      setShowTimer(true);
      // 새 질문이 왔으므로 저장된 시간 초기화
      setSavedTimeLeft(null);
    }
    // 첫 진입 시 또는 URL 직접 접근 시: 메시지가 있고, 로딩 중이 아니며, active 상태일 때
    else if (
      messagesLength > 0 &&
      !loading &&
      isActiveStatus &&
      !showTimer &&
      interviewId
    ) {
      setTimerKey((prev) => prev + 1);
      setShowTimer(true);
    }

    setPrevLoading(loading);
    setPrevMessagesLength(messagesLength);
  }, [
    loading,
    prevLoading,
    messages,
    interview?.status,
    prevMessagesLength,
    showTimer,
    interviewId,
    isResuming,
  ]);

  // 타이머 종료 시 자동 답변
  const handleTimerEnd = useCallback(() => {
    setShowTimer(false);
    if (!loading && interview?.status === "active") {
      submitAnswer("답변 시간 초과");
    }
  }, [loading, interview?.status, submitAnswer]);

  // 타이머 잔여 시간 업데이트 (매초 호출되어 현재 잔여 시간 저장)
  const handleTimerFinish = useCallback((timeLeft: number) => {
    setCurrentTimeLeft(timeLeft);
  }, []);

  // 면접 중단
  const handlePause = useCallback(async () => {
    try {
      setSavedTimeLeft(currentTimeLeft);
      await pauseInterview();
      setShowTimer(false);
    } catch (error) {
      console.error("면접 중단 실패:", error);
    }
  }, [pauseInterview, currentTimeLeft]);

  // 면접 이어서 시작
  const handleResume = useCallback(async () => {
    try {
      // 이어서 시작 플래그 설정 (useEffect에서 타이머 리셋 방지)
      setIsResuming(true);
      await resumeInterview();
      // 타이머 재시작 (저장된 잔여 시간으로)
      setTimerKey((prev) => prev + 1);
      setShowTimer(true);
    } catch (error) {
      setIsResuming(false);
      console.error("면접 재개 실패:", error);
    }
  }, [resumeInterview]);

  // 면접 처음부터 시작 (같은 주제로 새 면접 생성)
  const handleRestart = useCallback(async () => {
    try {
      const result = await cloneInterview();
      if (result?.interviewId) {
        navigate(`/socrates/${result.interviewId}`);
      }
    } catch (error) {
      console.error("면접 복제 실패:", error);
    }
  }, [cloneInterview, navigate]);

  // 답변 제출 핸들러
  const handleSubmit = useCallback(
    async (content: string) => {
      if (!content.trim() || loading) return;

      try {
        // 기기 등록 판정 통과 후에 입력창/타이머를 초기화한다
        await submitAnswerWithDeviceGuard(content, () => {
          setInputValue("");
          setShowTimer(false);
        });
      } catch (error) {
        console.error("답변 제출 실패:", error);
      }
    },
    [submitAnswerWithDeviceGuard, loading],
  );

  // 입력 값 변경 핸들러
  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
  }, []);

  // 면접 상태
  const isActive = interview?.status === "active";
  const isPaused = interview?.status === "paused";
  const isCompleted = interview?.status === "completed";

  // 면접이 완료되었거나 ID가 없으면 입력 비활성화
  // 튜터와 동일하게 loading, listening 상태에서도 텍스트 입력 비활성화
  const isDisabled =
    !interviewId ||
    isCompleted ||
    isPaused ||
    loading ||
    listening ||
    isCheckingDevice;

  // Prompt 상태 결정 (튜터와 동일한 로직)
  const getPromptState = (): "default" | "typing" | "listening" | "loading" => {
    // 기기 등록 판정 왕복 동안에도 로딩으로 처리해야 연타 제출을 막을 수 있다
    if (loading || isCheckingDevice) return "loading";
    if (listening) return "listening";
    if (inputValue.length > 0) return "typing";
    return "default";
  };

  // 결과 보기 핸들러
  const handleShowResult = useCallback(async () => {
    if (!interviewId) return;

    // 결과가 없으면 생성 후 이동
    if (!interview?.result) {
      try {
        await generateResult();
      } catch (error) {
        console.error("결과 생성 실패:", error);
        return;
      }
    }

    navigate(`/socrates/${interviewId}/result`);
  }, [interviewId, interview?.result, generateResult, navigate]);

  // 면접 완료 상태일 때는 결과 버튼 즉시 표시 (결과 생성은 버튼 클릭 시)
  if (isCompleted) {
    return (
      <div className="w-full max-w-[800px] flex flex-col gap-2 p-4">
        <div className="flex gap-3 items-center w-full">
          {/* 다시 면접보기 버튼 */}
          <button
            onClick={handleRestart}
            disabled={loading || isGeneratingResult}
            className="flex-1 h-[48px] rounded-[24px] border border-[#ccc] bg-white text-[#666] font-medium text-[16px] tracking-[-0.32px] disabled:opacity-50"
          >
            다시 면접보기
          </button>
          {/* 결과 보기 버튼 */}
          <button
            onClick={handleShowResult}
            disabled={isGeneratingResult}
            className="flex-1 h-[48px] rounded-[24px] bg-gradient-to-r from-[#cd3ec7] via-[#5328df] to-[#2d8efa] hover:from-[#ab38a6] hover:via-[#4927b9] hover:to-[#2b78cf] text-white font-bold text-[16px] tracking-[-0.32px] transition-all duration-200 disabled:opacity-50"
          >
            {isGeneratingResult ? "결과 생성 중..." : "결과 보기"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[800px] flex flex-col">
      {/* 면접 중단 버튼 - 진행 중일 때 */}
      {messages &&
        messages.length > 0 &&
        !isCompleted &&
        isActive &&
        !loading && (
          <div className="flex justify-center w-full px-4">
            {isMobile ? (
              <button
                onClick={handlePause}
                className="px-3 py-0 h-[28px] rounded-[20px] bg-gradient-to-r from-[#cd3ec7] via-[#5328df] to-[#2d8efa] text-white text-[14px] font-semibold tracking-[-0.28px] leading-[20px]"
              >
                면접 중단
              </button>
            ) : (
              <Btn.Primary
                onClick={handlePause}
                className="!px-6 !py-2 !h-[40px] !min-h-0 !rounded-full !bg-[#6D2EF2] max-w-[200px]"
              >
                면접 중단
              </Btn.Primary>
            )}
          </div>
        )}

      {/* 일시정지 상태일 때 - 입력 필드 위에 버튼 표시 */}
      {isPaused && (
        <div className="flex gap-3 items-center w-full px-4 pb-4">
          <button
            onClick={handleRestart}
            disabled={loading}
            className="flex-1 h-[48px] rounded-[24px] border border-[#ccc] bg-white text-[#666] font-medium text-[16px] tracking-[-0.32px] disabled:opacity-50"
          >
            처음부터 시작
          </button>
          <button
            onClick={handleResume}
            disabled={loading}
            className="flex-1 h-[48px] rounded-[24px] bg-[#6d2ef2] text-white font-bold text-[16px] tracking-[-0.32px] disabled:opacity-50"
          >
            이어서 시작
          </button>
        </div>
      )}

      {/* 프로그레스바 + 입력창 통합 컨테이너 (Figma 디자인) */}
      <div className="w-full flex flex-col gap-1">
        {/* 타이머 프로그레스 바 - 입력창 바로 위에 위치 */}
        {showTimer &&
          messages &&
          messages.length > 0 &&
          isActive &&
          !loading && (
            <div className="w-full px-5">
              <ProgressBar
                time={TIMER_DURATION}
                handleTimer={handleTimerEnd}
                onFinish={handleTimerFinish}
                resetKey={timerKey}
                initialTime={savedTimeLeft ?? TIMER_DURATION}
              />
            </div>
          )}

        {!isPaused && (
          <Prompt
            value={inputValue}
            onChange={handleInputChange}
            onSubmit={handleSubmit}
            onStopClick={handlePause}
            state={getPromptState()}
            onMicClick={handleMicClick}
            onCloseClick={handleCloseClick}
            placeholder={
              !interviewId
                ? "면접을 시작하려면 위의 카드를 선택하세요"
                : isCompleted
                ? "면접이 완료되었습니다"
                : showTimer && isActive && !loading
                ? "2분 안에 자유롭게 답변해 보세요!"
                : "답변을 입력하세요..."
            }
            disabled={isDisabled}
          />
        )}
      </div>
    </div>
  );
};

export default SocratesPromptContainer;
